import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { getUserRole } from "../utils/Auth";
import API_BASE_URL from "../config";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import CreateTaskModal from "./CreateTaskModal";
import FeedbackModal from "./FeedbackModal";
import TaskDetailsModal from "./TaskDetailsModal";
import { toast, ToastContainer } from "react-toastify";
import { User } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTheme } from "../context/ThemeContext";
import { Plus } from "lucide-react";

const datepickerPortalId = "datepicker-root";

const statusColumns = [
  { key: "Nou", label: "🆕 Nou" },
  { key: "Vazut", label: "👀 Văzut" },
  { key: "InProductie", label: "⚙️ În producție" },
  { key: "Finalizat", label: "🏁 Finalizat" },
];

const priorityColors = {
  Urgent: "border-red-500",
  Mare: "border-orange-500",
  Medie: "border-yellow-500",
  Mica: "border-green-500",
};

const priorityLabels = {
  Urgent: "Urgent",
  Mare: "Mare",
  Medie: "Medie",
  Mica: "Mică",
};

const priorityMap = {
  0: "Urgent",
  1: "Mare",
  2: "Medie",
  3: "Mica",
};

const statusOrder = ["Nou", "Vazut", "InProductie", "Finalizat"];

const TasksBoard = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [pendingFinalization, setPendingFinalization] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [assignTaskId, setAssignTaskId] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [pendingDate, setPendingDate] = useState({});
  const token = localStorage.getItem("token");
  const role = getUserRole();
  const [selectedUserId, setSelectedUserId] = useState("self");
  const { darkMode, toggleTheme } = useTheme();

  const statusMap = {
    0: "Nou",
    1: "Vazut",
    2: "InProductie",
    3: "Finalizat",
  };

  const fetchTasks = async () => {
    try {
      let url = "";

      if (role === "Admin") {
        if (selectedUserId === "all") {
          url = `${API_BASE_URL}/api/tasks/all`;
        } else if (selectedUserId === "self") {
          // 🔹 már ez az endpoint adja vissza a bejelentkezett user (admin) saját taskjait
          url = `${API_BASE_URL}/api/tasks`;
        } else {
          url = `${API_BASE_URL}/api/tasks/for-agent/${selectedUserId}`;
        }
      } else {
        // Agent esetén is ugyanaz
        url = `${API_BASE_URL}/api/tasks`;
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      const normalized = Array.isArray(data)
        ? data.map((t) => ({
          ...t,
          status:
            typeof t.status === "number"
              ? statusMap[t.status] || "Nou"
              : t.status,
          priority:
            typeof t.priority === "number"
              ? priorityMap[t.priority] || "Medie"
              : t.priority,
        }))
        : [];

      setTasks(normalized);
    } catch (err) {
      console.error("❌ Task fetch error:", err);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (token && role) {
      fetchTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, role, selectedUserId]);

  // ---- USER LISTA ----
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setUsers(data);
      } catch (err) {
        console.error("⚠️ User fetch error:", err);
      }
    };
    fetchUsers();
  }, [token]);

  // ---- ASSIGN USER ----
  const assignUserToTask = async (taskId, agentId) => {
    if (role !== "Admin") {
      toast.error("Doar administratorul poate atribui task-uri.");
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/assign`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ agentId }),
      });

      const data = await res.json();
      if (!res.ok || data.success === false) {
        toast.error(data.message || "Eroare la atribuirea utilizatorului.");
      } else {
        toast.success("Utilizator atribuit cu succes!", {
          toastId: "assign-success",
        });
        const user = users.find((u) => u.id === parseInt(agentId));
        setTasks((prev) =>
          prev.map((t) =>
            t.id === taskId ? { ...t, agentName: user?.name || "—" } : t
          )
        );
      }

    } catch (err) {
      console.error("⚠️ Assign user error:", err);
      toast.error("Eroare la server.");
    } finally {
      setAssignTaskId(null);
      setSearchTerm("");
    }
  };

  // ---- STATUS UPDATE ----
  const updateTaskStatus = async (id, newStatus, feedback = null) => {
    const statusToInt = {
      Nou: 0,
      Vazut: 1,
      InProductie: 2,
      Finalizat: 3,
    };

    // ✅ Optimista UI update – azonnal mozgatjuk
    setTasks(prev =>
      prev.map(t =>
        t.id === parseInt(id) ? { ...t, status: newStatus } : t
      )
    );

    try {
      const res = await fetch(`${API_BASE_URL}/api/tasks/${id}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          status: statusToInt[newStatus],
          feedback,
        }),
      });

      const data = await res.json();
      if (!res.ok || data.success === false) {
        toast.error(data.message || "Eroare la actualizarea statusului.", {
          toastId: "status-update-error",
        });
        return;
      }

      toast.success(data.message || "Status actualizat cu succes.", {
        toastId: "status-update-success",
      });

      // 🔥 Friss task adatok lehívása
      const refreshed = await fetch(`${API_BASE_URL}/api/tasks/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updatedTask = await refreshed.json();

      // ✅ Biztonságos merge (nem rontjuk el a status stringet)
      setTasks(prev =>
        prev.map(t =>
          t.id === parseInt(id)
            ? {
              ...t,
              startTime: updatedTask.startTime,
              completedAt: updatedTask.completedAt,
              feedback: updatedTask.feedback,
              updatedAt: updatedTask.updatedAt,
              // status marad string, de azért beállítjuk biztosra
              status: newStatus,
            }
            : t
        )
      );

      // ✅ Ha a modalban épp ez van megnyitva – frissítjük ott is
      setSelectedTask(prev =>
        prev && prev.id === parseInt(id)
          ? {
            ...prev,
            startTime: updatedTask.startTime,
            completedAt: updatedTask.completedAt,
            feedback: updatedTask.feedback,
            updatedAt: updatedTask.updatedAt,
            status: newStatus,
          }
          : prev
      );
    } catch (err) {
      console.error("⚠️ Update status error:", err);
      toast.error("Eroare la server.");
    }
  };

  // ---- DEADLINE UPDATE ----
  const updateDeadline = async (taskId, newDeadline) => {
    if (role !== "Admin") return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/deadline`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ deadline: newDeadline }),
      });

      const data = await res.json();
      if (!res.ok || data.success === false) {
        toast.error(data.message || "Eroare la actualizarea deadline-ului.");
        return;
      }

      toast.success("Deadline actualizat cu succes.", {
        toastId: "deadline-update-success",
      });

      // 🔥 Lokális frissítés – design megőrzéssel
      setTasks((prev) =>
        prev.map((t) =>
          t.id === taskId ? { ...t, deadline: newDeadline } : t
        )
      );
    } catch (err) {
      console.error("⚠️ Deadline update error:", err);
      toast.error("Eroare la server.");
    }
  };

  // ---- DRAG & DROP ----
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const { draggableId, destination, source } = result;
    const newStatus = destination.droppableId;
    if (newStatus === source.droppableId) return;

    const fromIndex = statusOrder.indexOf(source.droppableId);
    const toIndex = statusOrder.indexOf(newStatus);

    if (toIndex < fromIndex) {
      toast.error("Nu poți muta task-ul înapoi în starea anterioară!", {
        toastId: "cannot-move-back",
      });
      return;
    }

    if (newStatus === "Finalizat") {
      const task = tasks.find((t) => t.id === parseInt(draggableId));
      setPendingFinalization(task);
      setFeedbackModalOpen(true);
      return;
    }

    updateTaskStatus(draggableId, newStatus);
  };

  // ---- FEEDBACK ----
  const handleFeedbackSubmit = (feedback) => {
    if (!pendingFinalization) return;
    updateTaskStatus(pendingFinalization.id, "Finalizat", feedback);
    setFeedbackModalOpen(false);
    setPendingFinalization(null);
  };

  const handleFeedbackCancel = () => {
    setFeedbackModalOpen(false);
    setPendingFinalization(null);
  };

  // ---- SZŰRÉS ----
  const filteredUsers =
    searchTerm.trim() === ""
      ? users
      : users.filter((u) =>
        u.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

  // ---- UI ----
  if (loading)
    return <div className="text-center text-gray-500 mt-10">Se încarcă...</div>;

  if (!document.getElementById(datepickerPortalId)) {
    const el = document.createElement("div");
    el.id = datepickerPortalId;
    document.body.appendChild(el);
  }

  return (
    <div
      className={`p-6 min-h-screen transition-colors duration-300 overflow-x-hidden w-full box-border ${darkMode ? "bg-gray-900" : "bg-gray-200"
        }`}
    >
      <ToastContainer
        position="bottom-center"
        autoClose={2500}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
        theme={darkMode ? "dark" : "light"}
        toastStyle={{
          color: darkMode ? "#ffffff" : "#000000",
          backgroundColor: darkMode ? "#333333" : "#ffffff",
        }}
      />

      {/* HEADER */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h2
          className={`text-3xl font-bold ${darkMode ? "text-gray-100" : "text-gray-800"
            }`}
        >
          🗂️ Task Board
        </h2>


        <div className="flex flex-wrap items-center gap-3">
          {/* Modern Filter Button */}
          {role === "Admin" && (
            <div className="relative">
              <button
                className={`flex items-center justify-between w-[240px] h-[44px] px-4 rounded-xl shadow-sm transition-all duration-200 border-2 ${darkMode
                  ? "bg-gray-800 text-gray-200 border-gray-600 hover:bg-gray-700"
                  : "bg-white text-gray-800 border-gray-300 hover:bg-gray-100"
                  }`}

                onClick={(e) => {
                  const dropdown = document.getElementById("filter-dropdown");
                  dropdown.classList.toggle("hidden");
                }}
              >

                <div className="flex items-center gap-2 truncate">
                  <User className="w-5 h-5 shrink-0" />
                  <span className="font-semibold text-sm truncate max-w-[130px]">
                    {selectedUserId === "self"
                      ? "🔹 Task-urile mele"
                      : selectedUserId === "all"
                        ? "🌍 Toate task-urile"
                        : (() => {
                          const u = users.find((x) => String(x.id) === String(selectedUserId));
                          return u ? `${u.name}` : "Filtru utilizator";
                        })()}
                  </span>
                </div>

                <svg
                  className="w-4 h-4 ml-2 opacity-80 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Content */}
              <div
                id="filter-dropdown"
                className={`hidden absolute right-0 mt-2 w-60 z-50 rounded-xl shadow-lg animate-fade-in border ${darkMode
                  ? "bg-gray-800 border-gray-700 text-gray-100"
                  : "bg-white border-gray-200 text-gray-800"
                  }`}
              >

                <div className="p-2">
                  <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 px-2 py-1">
                    Vizualizare
                  </div>
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => {
                        setSelectedUserId("self");
                        document.getElementById("filter-dropdown").classList.add("hidden");
                      }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition ${selectedUserId === "self"
                        ? darkMode
                          ? "bg-blue-900 text-blue-300 font-medium"
                          : "bg-blue-100 text-blue-600 font-medium"
                        : darkMode
                          ? "hover:bg-gray-700 text-gray-200"
                          : "hover:bg-gray-100 text-gray-700"
                        }`}

                    >
                      🔹 Task-urile mele
                    </button>

                    <button
                      onClick={() => {
                        setSelectedUserId("all");
                        document.getElementById("filter-dropdown").classList.add("hidden");
                      }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition ${selectedUserId === "all"
                        ? darkMode
                          ? "bg-blue-900 text-blue-300 font-medium"
                          : "bg-blue-100 text-blue-600 font-medium"
                        : darkMode
                          ? "hover:bg-gray-700 text-gray-200"
                          : "hover:bg-gray-100 text-gray-700"
                        }`}
                    >
                      🌍 Toate task-urile
                    </button>
                  </div>

                  <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 mt-3 px-2 py-1">
                    Utilizatori
                  </div>
                  <div className="max-h-60 overflow-y-auto custom-scrollbar">
                    {users
                      .filter((u) => u.id !== parseInt(localStorage.getItem("userId")))
                      .map((u) => (
                        <button
                          key={u.id}
                          onClick={() => {
                            setSelectedUserId(u.id);
                            document.getElementById("filter-dropdown").classList.add("hidden");
                          }}
                          className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-md text-sm transition ${selectedUserId === String(u.id)
                            ? darkMode
                              ? "bg-blue-900 text-blue-300 font-medium"
                              : "bg-blue-100 text-blue-600 font-medium"
                            : darkMode
                              ? "hover:bg-gray-700 text-gray-200"
                              : "hover:bg-gray-100 text-gray-700"
                            }`}
                        >
                          <User
                            className={`w-4 h-4 opacity-80 ${darkMode ? "text-blue-400" : "text-blue-500"
                              }`}
                          />
                          <span>{u.name}</span>
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Modern Create Button */}
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center justify-center gap-2 w-[240px] h-[44px]
             bg-gradient-to-r from-emerald-400 to-green-500
             hover:from-emerald-500 hover:to-green-600
             dark:from-emerald-600 dark:to-green-700
             text-white font-semibold text-sm
             rounded-xl shadow-sm transition-all duration-200
             focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:focus:ring-emerald-700"
          >
            <Plus className="w-4 h-4" />
            <span>Crează task nou</span>
          </button>
        </div>
      </div>

      {/* DND GRID */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-6 overflow-x-auto pb-4">
          {statusColumns.map((col) => (
            <Droppable key={col.key} droppableId={col.key}>
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={`flex-1 w-[260px] rounded-xl shadow-md border p-4 flex flex-col ${darkMode
                    ? "bg-gray-800 border-gray-700 text-gray-200 shadow-[0_4px_15px_rgba(0,0,0,0.3)] hover:shadow-[0_6px_25px_rgba(0,0,0,0.45)]"
                    : "bg-white border-gray-200 text-gray-800 shadow-[0_4px_15px_rgba(0,0,0,0.08)] hover:shadow-[0_6px_25px_rgba(0,0,0,0.18)]"
                    }`} style={{ height: "calc(100vh - 220px)" }}
                >
                  <h3
                    className={`sticky top-0 text-center text-base font-semibold mb-3 py-1 border-b ${darkMode
                      ? "bg-gray-800 border-gray-700 text-gray-100"
                      : "bg-white border-gray-200 text-gray-800"
                      }`}
                  >
                    {col.label}
                  </h3>

                  <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar">
                    {[...tasks]
                      .filter((t) => t.status === col.key)
                      .sort((a, b) => {
                        if (col.key === "Nou") {
                          const order = { Urgent: 0, Mare: 1, Medie: 2, Mica: 3 };
                          return order[a.priority] - order[b.priority];
                        }
                        return 0;
                      })
                      .map((task, index) => (
                        <Draggable
                          key={task.id}
                          draggableId={task.id.toString()}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              ref={provided.innerRef}
                              onDoubleClick={() => {
                                setSelectedTask(task);
                                setDetailsModalOpen(true);
                              }}
                              className={`relative p-3 mb-3 rounded-xl border-l-4 cursor-pointer transition-all duration-200
  ${darkMode
                                  ? "bg-gray-700 text-gray-100 shadow-md hover:shadow-lg hover:scale-[1.02]"
                                  : "bg-gray-50 text-gray-900 border border-gray-300 shadow-sm hover:bg-gray-100 hover:shadow-md hover:scale-[1.02]"
                                }
  ${priorityColors[task.priority] || "border-blue-500"}
  ${snapshot.isDragging ? "opacity-80 scale-[1.03]" : ""}
`}

                              style={{
                                height: "150px",
                                ...provided.draggableProps.style,
                              }}
                            >
                              <div
                                className={`font-semibold text-sm truncate ${darkMode ? "text-gray-100" : "text-gray-800"
                                  }`}
                              >
                                {task.title}
                              </div>

                              {task.description && (
                                <div
                                  className={`text-xs mt-1 overflow-hidden text-ellipsis line-clamp-3 cursor-help max-w-[220px] break-words ${darkMode ? "text-gray-300" : "text-gray-600"
                                    }`}
                                  title={task.description}
                                >

                                  {task.description.length > 180
                                    ? task.description.slice(0, 180) + "…"
                                    : task.description}
                                </div>
                              )}


                              <div
                                className={`text-xs mt-2 flex justify-between items-end ${darkMode ? "text-gray-400" : "text-gray-500"
                                  }`}
                              >
                                <span
                                  className={`truncate font-semibold ${task.priority === "Urgent"
                                    ? "text-red-500"
                                    : task.priority === "Mare"
                                      ? "text-orange-500"
                                      : task.priority === "Medie"
                                        ? "text-yellow-500"
                                        : "text-green-500"
                                    }`}
                                >
                                  {priorityLabels[task.priority] || task.priority}
                                </span>
                              </div>
                              <div className="absolute bottom-2 left-2 right-2 flex justify-between items-center text-sm font-medium z-10">
                                <div className="flex items-center gap-1 text-blue-700 dark:text-blue-300">
                                  {role === "Admin" ? (
                                    <div className="relative">
                                      {assignTaskId === task.id ? (
                                        <>
                                          <input
                                            autoFocus
                                            type="text"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            onBlur={() => setTimeout(() => setAssignTaskId(null), 200)}
                                            placeholder="Caută utilizator..."
                                            className={`w-40 px-2 py-1.5 text-xs rounded-lg border shadow-sm focus:outline-none transition-all
    ${darkMode
                                                ? "bg-gray-800 border-blue-500 text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-400"
                                                : "bg-white border-blue-400 text-gray-800 placeholder-gray-500 focus:ring-2 focus:ring-blue-400"
                                              }`}
                                          />


                                          {assignTaskId === task.id && filteredUsers.length > 0 &&
                                            ReactDOM.createPortal(
                                              <div
                                                className={`absolute z-[9999] rounded-xl border shadow-xl overflow-y-auto max-h-44
      animate-fade-in transition-all duration-200 scrollbar-thin scrollbar-track-transparent
      [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full
      ${darkMode
                                                    ? "bg-gray-900 border-gray-700 text-gray-100 [&::-webkit-scrollbar-thumb]:bg-blue-600"
                                                    : "bg-gray-300 border-gray-300 text-gray-800 shadow-[0_2px_12px_rgba(0,0,0,0.08)] [&::-webkit-scrollbar-thumb]:bg-blue-400"
                                                  }`}
                                                style={{
                                                  top: dropdownPosition.top,
                                                  left: dropdownPosition.left,
                                                  width: "165px",
                                                }}
                                              >
                                                {filteredUsers.map((u) => (
                                                  <div
                                                    key={u.id}
                                                    onMouseDown={() => assignUserToTask(task.id, u.id)}
                                                    className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 transition-colors duration-150
            ${darkMode
                                                        ? "hover:bg-blue-900/50 text-gray-100"
                                                        : "hover:bg-blue-200/70 text-gray-900"
                                                      }`}
                                                  >
                                                    <User className={`w-4 h-4 ${darkMode ? "text-blue-400" : "text-blue-600"}`} />
                                                    <span className="truncate">{u.name}</span>
                                                  </div>
                                                ))}
                                              </div>,
                                              document.body
                                            )
                                          }


                                        </>
                                      ) : (
                                        <div
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            setDropdownPosition({
                                              top: rect.bottom + window.scrollY + 4,
                                              left: rect.left + window.scrollX,
                                            });
                                            setAssignTaskId(task.id);
                                          }}
                                          className={`flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer transition ${darkMode
                                            ? "text-blue-300 bg-gray-800/50 hover:bg-gray-900"
                                            : "text-blue-800 bg-blue-100/60 hover:bg-blue-300"
                                            }`}
                                          title="Atribuie utilizator (Admin only)"
                                        >
                                          <User className={`w-4 h-4 ${darkMode ? "text-blue-300" : "text-blue-600"}`} />
                                          <span className="truncate max-w-[120px]">{task.agentName || "—"}</span>
                                        </div>

                                      )}
                                    </div>
                                  ) : (
                                    <span className={`${darkMode ? "text-blue-300" : "text-blue-800 font-semibold"}`}>
                                      {task.agentName || "—"}
                                    </span>
                                  )}

                                </div>

                                {/* RIGHT SIDE - deadline */}
                                <div className="relative flex items-center gap-1 text-xs text-red-500 whitespace-nowrap">
                                  {role === "Admin" ? (
                                    <>
                                      <div className="flex items-center">
                                        <DatePicker
                                          selected={
                                            task.deadline
                                              ? new Date(
                                                task.deadline.endsWith("Z")
                                                  ? task.deadline
                                                  : task.deadline + "Z"
                                              )
                                              : null
                                          }
                                          onChange={(date) => {
                                            if (!date) return;
                                            const utcDate = date.toISOString();
                                            updateDeadline(task.id, utcDate);
                                          }}
                                          showTimeSelect
                                          timeFormat="HH:mm"
                                          timeIntervals={15}
                                          dateFormat="yyyy-MM-dd HH:mm"
                                          popperPlacement="bottom-end"
                                          portalId={datepickerPortalId}
                                          className="bg-transparent text-red-500 text-xs font-medium cursor-pointer w-[110px]"
                                          calendarClassName={`shadow-lg rounded-lg border ${darkMode
                                            ? "border-gray-700 bg-gray-800 text-gray-100"
                                            : "border-gray-300 bg-white text-gray-900"
                                            }`}
                                          customInput={
                                            <div
                                              className={`w-full flex items-center justify-end py-1 px-2 rounded-md 
  cursor-pointer transition select-none
  ${darkMode
                                                  ? "hover:bg-gray-800 text-red-400"
                                                  : "hover:bg-orange-200 text-orange-600"
                                                }`}
                                              title="Modifică deadline (Admin only)"
                                            >
                                              <span className="text-red-500">📅</span>
                                              <span className="text-red-500 text-xs font-medium">
                                                {task.deadline
                                                  ? new Date(
                                                    task.deadline.endsWith("Z")
                                                      ? task.deadline
                                                      : task.deadline + "Z"
                                                  ).toLocaleString("ro-RO", {
                                                    year: "numeric",
                                                    month: "2-digit",
                                                    day: "2-digit",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                  })
                                                  : "—"}
                                              </span>
                                            </div>
                                          }
                                        />
                                      </div>

                                    </>
                                  ) : (
                                    task.deadline && (
                                      <div
                                        className={`flex items-center justify-end py-1 px-2 rounded-md select-none
    ${darkMode
                                            ? "text-red-400"
                                            : "text-orange-600"
                                          }`}
                                        title={new Date(
                                          task.deadline.endsWith("Z") ? task.deadline : task.deadline + "Z"
                                        ).toLocaleString("ro-RO", {
                                          year: "numeric",
                                          month: "2-digit",
                                          day: "2-digit",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                      >
                                        <span className="text-red-500 mr-1">📅</span>
                                        <span className="text-red-500 text-xs font-medium">
                                          {new Date(
                                            task.deadline.endsWith("Z") ? task.deadline : task.deadline + "Z"
                                          ).toLocaleString("ro-RO", {
                                            year: "numeric",
                                            month: "2-digit",
                                            day: "2-digit",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })}
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>

                              </div>

                            </div>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {/* CREATE MODAL */}
      <CreateTaskModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onTaskCreated={(task) => {
          const normalized = {
            ...task,
            status:
              typeof task.status === "number"
                ? statusMap[task.status] || "Nou"
                : task.status,
            priority:
              typeof task.priority === "number"
                ? priorityMap[task.priority] || "Medie"
                : task.priority,
          };
          setTasks((prev) => [normalized, ...prev]);
        }}
      />

      {/* FEEDBACK MODAL */}
      {feedbackModalOpen && (
        <FeedbackModal
          isOpen={feedbackModalOpen}
          onClose={handleFeedbackCancel}
          onSubmit={handleFeedbackSubmit}
        />
      )}

      {/* DETAILS MODAL */}
      <TaskDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        task={selectedTask}
        onTaskUpdated={(id, updatedFields) => {
          if (updatedFields.archived) {
            // archivált taskot töröljük a lokális listából
            setTasks(prev => prev.filter(t => t.id !== id));
            setDetailsModalOpen(false);
            return;
          }

          // normál update
          setTasks(prev =>
            prev.map(t => (t.id === id ? { ...t, ...updatedFields } : t))
          );
          setSelectedTask(prev =>
            prev && prev.id === id ? { ...prev, ...updatedFields } : prev
          );
        }}
      />

    </div>
  );
};

export default TasksBoard;
